import React, { useState } from 'react'
import { Alert } from 'react-bootstrap'
import { Modal } from '../Modal/Modal'
import { Spinner } from '../Spinner/Spinner'
import { HumanInputFormView } from './HumanInputFormView'
import { hintsFromSchema, renderWorkflowOutputs } from './workflowOutputs'
import { isRunFinished, runStatusLabel } from './runStatus'
import {
  CustomWorkflowRunInputValue,
  useGetCustomWorkflowQuery,
  useGetCustomWorkflowRunFormQuery,
  useGetCustomWorkflowRunQuery,
  useSubmitCustomWorkflowRunFormMutation,
} from '../../store/customWorkflow/customWorkflows'

export interface ProvideInputModalProps {
  accountId: string
  workflowId: string
  runId: string
  title?: string
  show: boolean
  onHide: () => void
}

export const ProvideInputModal: React.FC<ProvideInputModalProps> = ({ accountId, workflowId, runId, title, show, onHide }) => {
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pollDone, setPollDone] = useState(false)

  const identity = `${accountId}|${workflowId}|${runId}`
  const [prevIdentity, setPrevIdentity] = useState(identity)
  if (prevIdentity !== identity) {
    setPrevIdentity(identity)
    setSubmitted(false)
    setSubmitError(null)
    setPollDone(false)
  }

  const {
    data: form,
    isLoading: formLoading,
    isError: formError,
  } = useGetCustomWorkflowRunFormQuery({ accountId, workflowId, runId }, { skip: !show || submitted })

  const [submitForm, { isLoading: submitting }] = useSubmitCustomWorkflowRunFormMutation()

  const { data: run } = useGetCustomWorkflowRunQuery(
    { accountId, workflowId, runId },
    { skip: !submitted || !show || pollDone, pollingInterval: 2000, skipPollingIfUnfocused: true },
  )
  const { data: workflow } = useGetCustomWorkflowQuery({ accountId, workflowId }, { skip: !show })
  const finished = !!run && isRunFinished(run.status)
  if (finished && !pollDone) {
    setPollDone(true)
  }

  const onSubmit = async (actionChoice: string, inputs: Record<string, CustomWorkflowRunInputValue>) => {
    setSubmitError(null)
    try {
      await submitForm({ accountId, workflowId, runId, inputs, action: actionChoice }).unwrap()
      setSubmitted(true)
    } catch {
      setSubmitError('Failed to submit the input — it may have been answered already.')
    }
  }

  const body = (
    <>
      {!submitted && formLoading && <Spinner size='sm' />}
      {!submitted && formError && (
        <Alert variant='warning' className='mb-0'>
          No pending input was found for this run — it may have been answered already.
        </Alert>
      )}
      {!submitted && form && <HumanInputFormView accountId={accountId} workflowId={workflowId} form={form} submitting={submitting} onSubmit={onSubmit} />}
      {submitError && (
        <Alert variant='danger' className='mt-3 mb-0'>
          {submitError}
        </Alert>
      )}
      {submitted && !finished && (
        <div className='d-flex align-items-center zocks-gap-2'>
          <Spinner size='sm' />
          <span>Input submitted — waiting for the workflow to finish…</span>
        </div>
      )}
      {submitted && finished && run && (
        <div>
          <div className='fw-semibold mb-2'>Result — {runStatusLabel(run.status)}</div>
          {run.error ? (
            <Alert variant='danger' className='mb-0'>
              {run.error}
            </Alert>
          ) : (
            renderWorkflowOutputs(run.outputs, hintsFromSchema(workflow?.outputSchema), { accountId, workflowId, runId })
          )}
        </div>
      )}
    </>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<h5 className='mb-0'>{title ?? 'Provide input'}</h5>} body={body} bodyClassName='pb-4' />
}

export default ProvideInputModal
